import { useEffect } from 'react';
import { differenceInMinutes } from 'date-fns';
import { CURRENCY_USD } from 'constants/currency';
import { useGetLessonParticipantOrderDetailsLazyQuery } from 'types/generated/client';
import { delayedAction } from 'utils/client/delayedAction';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { getIsPlayerCancelRefundable } from 'utils/shared/user/getIsPlayerCancelRefundable';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import ModalPlayerCancelComplete from 'screens/LessonPage/ModalPlayerCancelComplete';
import ModalPlayerCancelPrompt from 'screens/LessonPage/ModalPlayerCancelPrompt';
import { LessonCancelRequestProps } from 'screens/PlayerLoggedInScreen/props';
import { EXIT_DURATION_SAFE_MS } from 'components/modals/Modal/Modal';

const LessonCancelRequest: React.FC<LessonCancelRequestProps> = ({
  lesson,
  open,
  handleClose,
  handleOpen,
  handleCancelSuccess,
}) => {
  if (!lesson) return;

  const viewer = useViewer();

  const [
    getLessonParticipantOrderDetailsLazyQuery,
    {
      data: participantOrderDetailsData,
      loading: participantOrderDetailsLoading,
      called: participantOrderDetailsCalled,
    },
  ] = useGetLessonParticipantOrderDetailsLazyQuery();

  const playerCancelCompleteModal = useModal();

  const startDateTime = lesson?.startDateTime;

  const isPlayerCancelRefundable = startDateTime
    ? getIsPlayerCancelRefundable({
        lessonStartDateTime: new Date(startDateTime),
      })
    : false;

  const startTimeDifferenceMinutes = startDateTime
    ? differenceInMinutes(new Date(startDateTime), new Date())
    : 0;

  const participant = participantOrderDetailsData?.lessonParticipants[0];
  const refundPrice = participant?.orderItems[0]?.order.paidUnitAmount;

  const refundFormattedPrice =
    !!refundPrice &&
    convertUnitPriceToFormattedPrice(
      refundPrice,
      lesson?.ownerProfile?.stripeMerchantCurrency ||
        lesson?.ownerProfile?.currencyCoachDefault ||
        lesson?.currency ||
        CURRENCY_USD,
    );

  const displayRefundPrice = !!refundFormattedPrice ? refundFormattedPrice.priceDisplay : '';

  useEffect(() => {
    if (viewer.userId) {
      getLessonParticipantOrderDetailsLazyQuery({
        variables: { userId: viewer.userId, lessonId: lesson.id },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-and-network',
      });
    }
  }, [viewer.userId]);

  return (
    <>
      <ModalPlayerCancelPrompt
        isOpen={open}
        setIsOpen={(open) => (open ? handleOpen() : handleClose())}
        handleCancelComplete={() => {
          handleClose();
          handleCancelSuccess();
          // NOTE (2022-01-10): There is a bug with headlessui where dialogs cannot have siblings open (they must be nested). Causes these before the translation ends adds an overflow hidden to the html that doesn't go away and scrolling is stuck.
          delayedAction(EXIT_DURATION_SAFE_MS, () => playerCancelCompleteModal.openModal());
        }}
        lessonId={lesson?.id as string}
        paymentFulfillmentChannel={lesson?.paymentFulfillmentChannel}
        isPlayerCancelRefundable={isPlayerCancelRefundable}
        startTimeDifferenceMinutes={startTimeDifferenceMinutes}
        displayRefundPrice={displayRefundPrice}
        participantOrderDetails={participantOrderDetailsData}
        participantOrderDetailsLoading={participantOrderDetailsLoading}
        participantOrderDetailsCalled={participantOrderDetailsCalled}
      />
      <ModalPlayerCancelComplete
        isOpen={playerCancelCompleteModal.isOpen}
        setIsOpen={playerCancelCompleteModal.setIsOpen}
        displayRefundPrice={displayRefundPrice}
        isPlayerCancelRefundable={isPlayerCancelRefundable}
        paymentFulfillmentChannel={lesson.paymentFulfillmentChannel}
        handleDone={() => playerCancelCompleteModal.closeModal()}
      />
    </>
  );
};

export default LessonCancelRequest;
